import { usePurchasedProducts } from "@/hooks/usePurchasedProducts";
import { hasCustomerId } from "@/lib/api";
import {
  clearStoredSession,
  consumeLogoutRedirectPending,
  getTokenFromUrl,
  isShopifyLoggedIn,
  markLogoutRedirectPending,
} from "@/lib/emailStorage";
import type { DefaultPanelTab } from "@/lib/panelConfig";
import { getResolvedDefaultPanelTab } from "@/lib/panelConfig";
import { clearKnownCustomer, setKnownCustomer } from "@/lib/posthog";
import {
  getShopForTracking,
  trackBuyAgainWidgetInteract,
  trackBuyAgainWidgetView,
} from "@/lib/tracking";
import type { WidgetOpenSource } from "@/lib/widgetEvents";
import { widgetEventBus } from "@/lib/widgetEvents";
import { ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { BuyAgainPanel, hasSubscriptionStatus } from "./BuyAgainPanel";
import { ToastContainer } from "./Toast";
import { cn } from "@/lib/utils";
import {
  consumeWelcomeOfferApplyPending,
  isWelcomeCodeApplied,
  WELCOME_OFFER_ENABLED,
} from "@/lib/welcomeOffer";
import { isFabVisible } from "@/lib/fabVisibility";

// function getDisplayName(
//   customer:
//     | {
//         first_name?: string;
//         full_name?: string;
//       }
//     | null
//     | undefined,
// ): string {
//   const first = customer?.first_name?.trim();
//   const full = customer?.full_name?.trim();
//   return first ?? full ?? "";
// }

export function BuyAgainWidget() {
  // Consumed at mount: set just before we handed the customer to Shopify's
  // logout, which always lands on the homepage. Reopening the panel puts the
  // sign-in entry point back in front of them, which is why they logged out.
  const returnedFromLogout = useMemo(() => consumeLogoutRedirectPending(), []);
  const [isOpen, setIsOpen] = useState(
    () =>
      returnedFromLogout ||
      getTokenFromUrl() != null ||
      getResolvedDefaultPanelTab() != null,
  );
  const [forceOpen, setForceOpen] = useState(() => returnedFromLogout);
  const [overrideTab, setOverrideTab] = useState<DefaultPanelTab | undefined>(
    undefined,
  );
  const [openSource, setOpenSource] = useState<WidgetOpenSource | null>(() =>
    returnedFromLogout ||
    getTokenFromUrl() != null ||
    getResolvedDefaultPanelTab() != null
      ? "url"
      : null,
  );
  const [authVersion, setAuthVersion] = useState(0);
  // Floating pill: show the full greeting first, then collapse the name to fit.
  // const [pillNameCollapsed, setPillNameCollapsed] = useState(false);
  const defaultPanelTab = useMemo(() => getResolvedDefaultPanelTab(), []);
  // Latched at mount, before any effect runs: `?so-token` is stripped from the
  // URL by removeTokenFromUrl() once the API layer consumes it.
  const fabVisible = useMemo(() => isFabVisible(), []);

  const handleSignInSuccess = useCallback(() => {
    setAuthVersion((v) => v + 1);
  }, []);

  const { data, isLoading: productsLoading } =
    usePurchasedProducts(authVersion);

  const activeProducts = (data?.products ?? []).filter(
    (p) => p.status === "active" || p.status === "unlisted",
  );

  const userName = data?.customer?.first_name ?? "";
  const displayProducts = activeProducts.slice(0, 2);

  // The "Buy again" tab is temporarily hidden, so the panel only surfaces
  // subscriptions. Hide the FAB entirely when the customer has none.
  const hasSubscriptions = activeProducts.some(hasSubscriptionStatus);

  const shop = getShopForTracking(activeProducts);

  const handleLogout = useCallback(() => {
    if (shop) {
      trackBuyAgainWidgetInteract("widget_logout", shop);
    }
    clearStoredSession();
    clearKnownCustomer();

    // A storefront session outranks everything the widget stores, so clearing
    // our own keys would leave them signed in as the same customer. Send them
    // through Shopify's logout — classic customer accounts, so it lands back
    // on the homepage — and let the widget come up signed out.
    if (isShopifyLoggedIn()) {
      markLogoutRedirectPending();
      window.location.assign("/account/logout");
      return;
    }

    // Token mode: nothing to coordinate with the storefront. Bumping the auth
    // version re-runs the query, which `hasCustomerId()` now disables, so the
    // panel drops to its signed-out state in place. It stays open on purpose —
    // that state holds the sign-in entry point, and switching accounts is the
    // reason most people log out here.
    setAuthVersion((v) => v + 1);
  }, [shop]);

  // Subscribe to external open events (e.g. from PDP "1-click subscription management" link)
  useEffect(() => {
    return widgetEventBus.subscribe((event) => {
      if (event.type === "open") {
        setOverrideTab(event.tab);
        setOpenSource(event.source ?? "banner");
        setForceOpen(true);
        setIsOpen(true);
      }
    });
  }, []);

  // Consume the pending marker from the discount redirect so it doesn't
  // outlive the navigation, but don't auto-open the panel — the FAB's
  // "applied" state already communicates success.
  useEffect(() => {
    consumeWelcomeOfferApplyPending();
  }, []);

  useEffect(() => {
    const customer = data?.customer;
    if (!customer) return;
    setKnownCustomer({
      shopify_customer_id: customer.shopify_customer_id ?? null,
      email: customer.email ?? null,
      first_name: customer.first_name ?? null,
      last_name: customer.last_name ?? null,
    });
  }, [data?.customer, authVersion]);

  // Once the customer name is known, leave it fully visible briefly, then
  // collapse it back to the compact pill width.
  // useEffect(() => {
  //   if (!userName) return;
  //   setPillNameCollapsed(false);
  //   const t = window.setTimeout(() => setPillNameCollapsed(true), 3000);
  //   return () => window.clearTimeout(t);
  // }, [userName]);

  useEffect(() => {
    if (
      fabVisible &&
      shop &&
      !productsLoading &&
      !isOpen &&
      hasSubscriptions &&
      displayProducts.length > 0
    ) {
      trackBuyAgainWidgetView(activeProducts.length, shop);
    }
  }, [
    fabVisible,
    shop,
    productsLoading,
    isOpen,
    hasSubscriptions,
    displayProducts.length,
    activeProducts.length,
  ]);

  const handleOpenPanel = () => {
    if (shop) trackBuyAgainWidgetInteract("open_panel", shop);
    setOpenSource("icon");
    setIsOpen(true);
  };

  const handleClosePanel = () => {
    if (shop) trackBuyAgainWidgetInteract("close", shop);
    setIsOpen(false);
    setForceOpen(false);
    setOverrideTab(undefined);
    setOpenSource(null);
  };

  if (!forceOpen && productsLoading) {
    return null;
  }

  return (
    <>
      {isOpen ? (
        <BuyAgainPanel
          isOpen={isOpen}
          onClose={handleClosePanel}
          products={activeProducts}
          shop={shop}
          productsLoading={productsLoading}
          userName={userName || undefined}
          defaultPanelTab={overrideTab ?? defaultPanelTab}
          customer={data?.customer ?? null}
          openedFromSubscriptionLink={forceOpen}
          openSource={openSource}
          onSignInSuccess={handleSignInSuccess}
          onLogout={handleLogout}
          accountNotFound={data != null && data.customer == null}
        />
      ) : fabVisible && hasCustomerId() && hasSubscriptions ? (
        <div
          className={cn(
            "so-buy-again-widget-pop tw:fixed tw:bg-white/90 tw:backdrop-blur-md tw:rounded-md tw:rounded-r-none tw:rounded-b-none tw:bottom-0 tw:right-0 tw:z-40 tw:flex tw:gap-1 tw:items-center tw:cursor-pointer tw:border tw:border-black/10 tw:border-r-0 tw:border-b-0 tw:shadow-[0px_0px_16px_-7px_rgba(66,68,90,0.5)] tw:px-3",
            // Tighter vertical padding for the two-line stacks (applied / welcome)
            // so total height ≈ the single-line variants.
            WELCOME_OFFER_ENABLED &&
              (isWelcomeCodeApplied() || !hasCustomerId())
              ? "tw:py-1.5"
              : "tw:py-3",
          )}
          style={{ color: "var(--so-text, #523D33)" }}
          onClick={handleOpenPanel}
        >
          {WELCOME_OFFER_ENABLED && isWelcomeCodeApplied() ? (
            <>
              <span className="tw:text-xl tw:leading-none">⚡️</span>
              <div className="tw:flex tw:flex-col tw:items-start tw:leading-tight">
                <span
                  className="tw:text-sm tw:font-semibold"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Instant Access
                </span>
                <span
                  className="tw:text-xs tw:font-semibold tw:opacity-50"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  20% OFF applied
                </span>
              </div>
              <ChevronRight
                className="tw:size-4 tw:opacity-50"
                style={{ color: "var(--so-text, #523D33)" }}
              />
            </>
          ) : WELCOME_OFFER_ENABLED && !hasCustomerId() ? (
            <>
              <span className="tw:text-xl tw:leading-none">⚡️</span>
              <div className="tw:flex tw:flex-col tw:items-start tw:leading-tight">
                <span
                  className="tw:text-sm tw:font-semibold"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Instant Access
                </span>
                <span
                  className="tw:text-xs tw:font-semibold tw:opacity-50"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Welcome offer
                </span>
              </div>
              <ChevronRight
                className="tw:size-4 tw:opacity-50"
                style={{ color: "var(--so-text, #523D33)" }}
              />
            </>
          ) : (
            <>
              <span className="tw:text-md tw:leading-none">⚡️</span>
              <span
                className={cn(
                  "tw:text-sm tw:flex tw:items-center tw:font-bold tw:text-center tw:truncate tw:transition-[max-width] tw:duration-500",
                )}
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {userName ? `Welcome back, ${userName}` : "Instant Access"}
                <ChevronRight
                  className="tw:size-5 tw:opacity-50"
                  strokeWidth={2.5}
                  style={{ color: "var(--so-text, #523D33)" }}
                />
              </span>
            </>
          )}
        </div>
      ) : null}
      <ToastContainer />
    </>
  );
}
